/**
 * Ledger beams and single-tube beams, plus the diagonal braces.
 *
 * LB is a lattice — Ø34 chords at the lower and upper cup with verticals every
 * 360 mm. ERB is a single Ø48 tube with a vertical end plate joining its two
 * hooks. Both latch BOTH cups of the group (catalogue page *Enrørsbjelke*).
 */

import { Group, Mesh } from "three";
import { CFG, KL, KR, nodeY } from "../../data/haki.ts";
import { legAt, nearestMod } from "../../model/geometry.ts";
import type { Beam } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { boxAt, tubeBetween, V, yawOf } from "../threeHelpers.ts";
import type { RenderContext } from "./context.ts";

/** Verticals in an LB lattice, metres apart. */
const LATTICE_PITCH = 0.36;

/** How far the hooks sit inside the node centres, metres. */
const HOOK_INSET = 0.11;

function renderEndFittings(
  group: Group,
  beam: { a: { x: number; z: number }; b: { x: number; z: number }; kind: string },
  ux: number,
  uz: number,
  top: number,
  bot: number,
  rot: number,
): void {
  for (const [corner, sign] of [
    [beam.a, 1],
    [beam.b, -1],
  ] as const) {
    group.add(
      boxAt(
        corner.x + ux * sign * 0.055,
        top + 0.004,
        corner.z + uz * sign * 0.055,
        0.06,
        0.05,
        0.06,
        M.cast,
        rot,
      ),
    );
    group.add(
      boxAt(corner.x + ux * sign * 0.055, bot, corner.z + uz * sign * 0.055, 0.05, 0.04, 0.05, M.cast, rot),
    );

    if (beam.kind === "erb") {
      group.add(
        boxAt(
          corner.x + ux * sign * 0.06,
          (top + bot) / 2,
          corner.z + uz * sign * 0.06,
          0.02,
          top - bot + 0.1,
          0.05,
          M.cast,
          rot,
        ),
      );
    }

    group.add(
      boxAt(
        corner.x + ux * sign * 0.085,
        top + 0.036,
        corner.z + uz * sign * 0.085,
        0.028,
        0.016,
        0.04,
        M.cast,
        rot,
      ),
    );
  }
}

/** An orange sphere on every beam end with no standard under it. */
function markFreeEnds(ctx: RenderContext, beam: Beam, top: number): void {
  for (const corner of [beam.a, beam.b]) {
    if (legAt(ctx.model, corner.x, corner.z)) {
      continue;
    }

    const marker = new Mesh(G.sph, M.freeEnd);
    marker.scale.set(1.3, 1.3, 1.3);
    marker.position.set(corner.x, top, corner.z);
    marker.userData = { type: "freeEnd", x: corner.x, z: corner.z, p: beam.p };
    ctx.root.add(marker);
    ctx.pickEnds.push(marker);
  }
}

function bookBeam(ctx: RenderContext, beam: Beam): void {
  const table = beam.kind === "lb" ? CFG.lb : CFG.erb;
  const name = beam.kind === "lb" ? "Ledger beam LB " : "Single tube beam ERB ";
  const art = table.art[beam.len];
  const kg = table.kg[beam.len];

  if (art !== undefined && kg !== undefined) {
    ctx.bom.add("Beams", `${name}${beam.len} AL`, art, kg);
    return;
  }

  ctx.bom.add("Beams", `${name}${beam.len} (non-module)`, "—", 5);
}

export function renderBeams(ctx: RenderContext): void {
  ctx.model.beams.forEach((beam, idx) => {
    const yNode = nodeY(beam.p);
    const dx = beam.b.x - beam.a.x;
    const dz = beam.b.z - beam.a.z;
    const length = Math.hypot(dx, dz);
    const ux = dx / length;
    const uz = dz / length;
    const rot = yawOf(ux, uz);
    const inner = length - HOOK_INSET;
    const mx = (beam.a.x + beam.b.x) / 2;
    const mz = (beam.a.z + beam.b.z) / 2;
    const top = yNode + KR;
    const bot = yNode + KL;
    const pA = V(mx - (ux * inner) / 2, 0, mz - (uz * inner) / 2);
    const pB = V(mx + (ux * inner) / 2, 0, mz + (uz * inner) / 2);
    const group = new Group();

    if (beam.kind === "erb") {
      group.add(tubeBetween(pA.clone().setY(top), pB.clone().setY(top), G.tube48, M.alu));
    } else {
      group.add(tubeBetween(pA.clone().setY(top), pB.clone().setY(top), G.tube34, M.alu));
      group.add(tubeBetween(pA.clone().setY(bot), pB.clone().setY(bot), G.tube34, M.alu));

      const count = Math.max(1, Math.round(inner / LATTICE_PITCH));
      for (let i = 0; i <= count; i++) {
        const t = -inner / 2 + i * (inner / count);
        const depth = i === 0 || i === count ? 0.03 : 0.042;
        group.add(
          boxAt(mx + ux * t, (top + bot) / 2, mz + uz * t, 0.016, top - bot - 0.02, depth, M.alu, rot),
        );
      }
    }

    renderEndFittings(group, beam, ux, uz, top, bot, rot);

    for (const child of group.children) {
      child.userData = { type: "beam", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);

    markFreeEnds(ctx, beam, top);
    bookBeam(ctx, beam);
  });
}

/** True when any diagonal had to be booked as loose tube + couplers. */
export function renderDiagonals(ctx: RenderContext): boolean {
  let anyNonModule = false;

  ctx.model.diags.forEach((diag, idx) => {
    const dx = diag.b.x - diag.a.x;
    const dz = diag.b.z - diag.a.z;
    const length = Math.hypot(dx, dz);
    const ux = dx / length;
    const uz = dz / length;
    const offX = -uz * 0.06;
    const offZ = ux * 0.06;

    const pa = V(diag.a.x + offX, nodeY(diag.pa) + KR, diag.a.z + offZ);
    const pb = V(diag.b.x + offX, nodeY(diag.pb) + KR, diag.b.z + offZ);
    const dir = pb.clone().sub(pa).normalize();

    const group = new Group();
    group.add(
      tubeBetween(
        pa.clone().addScaledVector(dir, 0.05),
        pb.clone().addScaledVector(dir, -0.05),
        G.tube34,
        M.alu,
      ),
    );
    for (const point of [pa, pb]) {
      group.add(
        boxAt(point.x - offX / 2, point.y, point.z - offZ / 2, 0.075, 0.08, 0.075, M.cast, yawOf(ux, uz)),
      );
    }

    for (const child of group.children) {
      child.userData = { type: "diag", idx };
      if (child instanceof Mesh) {
        ctx.pickElems.push(child);
      }
    }
    ctx.root.add(group);

    const mod = nearestMod(length * 1000);
    const art = CFG.ds.art[mod];
    const kg = CFG.ds.kg[mod];
    if (art !== undefined && kg !== undefined && Math.abs(length * 1000 - mod) < 120) {
      ctx.bom.add("Bracing", `Diagonal brace DS ${mod} AL`, art, kg);
    } else {
      ctx.bom.add("Bracing", "Diagonal Ø48 tube + 2 couplers", "—", 4.5);
      anyNonModule = true;
    }
  });

  return anyNonModule;
}
